/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Inject, Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {configureLineItemList, enrichLineItemList} from './line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {OrderApiService} from '../order/order-api.service';
import {TeamApiService} from '../admin/team/team-api.service';
import {LineItemApiService} from './line-item-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalNetworkInfo} from '../../components/session/local-network-info';


export type LineItemListView = 'LINE_ITEM_VIEW' | 'ORDER_VIEW';

export interface LineItemEmbeddedListOptions {
  listId: string;
  destroyRef: DestroyRef;
  moreParams: Record<string, any>;
  view?: LineItemListView;
  advertiserId?: string;
  blockTeams?: boolean;
  onAfterFill?: (list: any) => void;
}

@Injectable({
  providedIn: 'root'
})
export class LineItemListSetupService {
  constructor(
    private readonly listService: SignalResourceListBindingService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly orderApi: OrderApiService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  configureList(list: any, view: LineItemListView = 'LINE_ITEM_VIEW'): void {
    if (!list.perms) {
      list.perms = this.LocalUserPermissions.getAllVisPermissions();
    }
    configureLineItemList(
      list,
      this.LocalNetworkProfile,
      this.adnTimeShifter,
      this.translate,
      this.appLocale,
      this.LocalUserPermissions,
      this.teamApi,
      this.orderApi,
      this.localNetworkInfo,
      view
    );
  }

  enrichList(list: any): void {
    enrichLineItemList(
      list,
      this.LocalNetworkProfile,
      this.adnTimeShifter,
      this.translate,
      this.appLocale,
      this.LocalUserPermissions,
      this.localNetworkInfo
    );
  }

  createEmbeddedList(options: LineItemEmbeddedListOptions): any {
    const list: any = {};
    const view = options.view || 'LINE_ITEM_VIEW';

    this.listService.create(this.LineItem, options.listId, options.destroyRef, {
      moreParams: options.moreParams,
      baseTable: 'lineItem',
      afterFill: () => {
        this.enrichList(list);
        options.onAfterFill?.(list);
      }
    }, list);

    list.perms = this.LocalUserPermissions.getAllVisPermissions();
    this.configureList(list, view);

    if (options.blockTeams) {
      list.blockTeams();
    }
    if (options.advertiserId) {
      list.assignAdvertiser(options.advertiserId);
    }

    return list;
  }
}
